/**
 * Bulk actions for the leads list: group editor (set fields, tag/interest
 * add-remove-replace, shared hotel attributes), bulk archive/restore and
 * superuser bulk purge. Mirrors the legacy group editor semantics: only
 * explicitly enabled fields are applied; enabling with an empty value clears.
 */

import React, { useState } from 'react';
import {
  useGetList,
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
  useUnselectAll,
} from 'react-admin';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  EditNote as GroupEditIcon,
  Archive as ArchiveIcon,
  Unarchive as RestoreIcon,
  DeleteForever as PurgeIcon,
} from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import { CANONICAL_STATUSES } from './LeadFilterInputs';

interface LookupChoice {
  id: number;
  name: string;
}

const listParams = {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: 'name', order: 'ASC' as const },
};

type SetMode = 'none' | 'add' | 'remove' | 'replace';

interface FieldState {
  enabled: boolean;
  value: LookupChoice | null;
}

const emptyField: FieldState = { enabled: false, value: null };

/** One "Modificar" row: checkbox + catalog autocomplete. */
const CatalogRow = ({
  label,
  resource,
  state,
  onChange,
}: {
  label: string;
  resource: string;
  state: FieldState;
  onChange: (next: FieldState) => void;
}) => {
  const { data } = useGetList<LookupChoice>(resource, listParams);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={state.enabled}
            onChange={(e) => onChange({ ...state, enabled: e.target.checked })}
            size="small"
          />
        }
        label=""
        sx={{ mr: 0 }}
      />
      <Autocomplete
        size="small"
        disabled={!state.enabled}
        options={data ?? []}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={state.value}
        onChange={(_, value) => onChange({ ...state, value })}
        renderInput={(params) => <TextField {...params} label={label} />}
        sx={{ flex: 1 }}
      />
    </Box>
  );
};

/** Tag/interest block: mode + multiselect. */
const SetItemsBlock = ({
  label,
  resource,
  mode,
  items,
  onMode,
  onItems,
  modeLabels,
}: {
  label: string;
  resource: string;
  mode: SetMode;
  items: LookupChoice[];
  onMode: (mode: SetMode) => void;
  onItems: (items: LookupChoice[]) => void;
  modeLabels: Record<SetMode, string>;
}) => {
  const { data } = useGetList<LookupChoice>(resource, listParams);
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
      <TextField
        select
        size="small"
        label={label}
        value={mode}
        onChange={(e) => onMode(e.target.value as SetMode)}
        sx={{ width: 170 }}
      >
        {(Object.keys(modeLabels) as SetMode[]).map((key) => (
          <MenuItem key={key} value={key}>{modeLabels[key]}</MenuItem>
        ))}
      </TextField>
      <Autocomplete
        multiple
        size="small"
        disabled={mode === 'none'}
        options={data ?? []}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={items}
        onChange={(_, value) => onItems(value)}
        renderInput={(params) => <TextField {...params} />}
        sx={{ flex: 1 }}
      />
    </Box>
  );
};

const BulkEditDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('leads');
  const { selectedIds } = useListContext();

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`resources.leads.bulk.${key}`, options);

  const [company, setCompany] = useState(emptyField);
  const [hotel, setHotel] = useState(emptyField);
  const [chain, setChain] = useState(emptyField);
  const [jobTitle, setJobTitle] = useState(emptyField);
  const [contactType, setContactType] = useState(emptyField);
  const [leadSource, setLeadSource] = useState(emptyField);
  const [countryEnabled, setCountryEnabled] = useState(false);
  const [country, setCountry] = useState('');
  const [statusEnabled, setStatusEnabled] = useState(false);
  const [status, setStatus] = useState('');

  const [tagMode, setTagMode] = useState<SetMode>('none');
  const [tagItems, setTagItems] = useState<LookupChoice[]>([]);
  const [interestMode, setInterestMode] = useState<SetMode>('none');
  const [interestItems, setInterestItems] = useState<LookupChoice[]>([]);

  const [hotelChainAttr, setHotelChainAttr] = useState(emptyField);
  const [hotelCategoryAttr, setHotelCategoryAttr] = useState(emptyField);
  const [pmsAttr, setPmsAttr] = useState(emptyField);
  const [lockAttr, setLockAttr] = useState(emptyField);
  const [processorAttr, setProcessorAttr] = useState(emptyField);

  const modeLabels: Record<SetMode, string> = {
    none: t('modeNone'),
    add: t('modeAdd'),
    remove: t('modeRemove'),
    replace: t('modeReplace'),
  };

  const hotelAttrsEnabled =
    hotelChainAttr.enabled || hotelCategoryAttr.enabled || pmsAttr.enabled
    || lockAttr.enabled || processorAttr.enabled;

  const fieldsEnabled =
    company.enabled || hotel.enabled || chain.enabled || jobTitle.enabled
    || contactType.enabled || leadSource.enabled || countryEnabled || statusEnabled;

  const handleApply = async () => {
    const operations: string[] = [];
    if (fieldsEnabled) operations.push(t('summaryFields'));
    if (tagMode !== 'none') operations.push(t('summaryTags'));
    if (interestMode !== 'none') operations.push(t('summaryInterests'));
    if (hotelAttrsEnabled) operations.push(t('summaryHotelAttrs'));
    if (operations.length === 0) return;

    if (!window.confirm(t('confirm', {
      count: selectedIds.length,
      operations: operations.join(', '),
    }))) {
      return;
    }

    try {
      if (fieldsEnabled) {
        await httpClient.post('/leads/bulk/update', {
          leadIds: selectedIds,
          setCompany: company.enabled, companyId: company.value?.id ?? null,
          setHotel: hotel.enabled, hotelId: hotel.value?.id ?? null,
          setHotelChain: chain.enabled, hotelChainId: chain.value?.id ?? null,
          setJobTitle: jobTitle.enabled, jobTitleId: jobTitle.value?.id ?? null,
          setContactType: contactType.enabled, contactTypeId: contactType.value?.id ?? null,
          setLeadSource: leadSource.enabled, leadSourceId: leadSource.value?.id ?? null,
          setCountry: countryEnabled, country: country || null,
          setStatus: statusEnabled, status: status || null,
        });
      }
      if (tagMode !== 'none') {
        await httpClient.post('/leads/bulk/tags', {
          leadIds: selectedIds,
          itemIds: tagItems.map((item) => item.id),
          mode: tagMode,
        });
      }
      if (interestMode !== 'none') {
        await httpClient.post('/leads/bulk/interests', {
          leadIds: selectedIds,
          itemIds: interestItems.map((item) => item.id),
          mode: interestMode,
        });
      }
      if (hotelAttrsEnabled) {
        const response = await httpClient.post<{ affectedHotels: number; affectedLeads: number }>(
          '/leads/bulk/hotel-attributes',
          {
            leadIds: selectedIds,
            setHotelChain: hotelChainAttr.enabled, hotelChainId: hotelChainAttr.value?.id ?? null,
            setHotelCategory: hotelCategoryAttr.enabled, hotelCategoryId: hotelCategoryAttr.value?.id ?? null,
            setPmsSystem: pmsAttr.enabled, pmsSystemId: pmsAttr.value?.id ?? null,
            setLockSystem: lockAttr.enabled, lockSystemId: lockAttr.value?.id ?? null,
            setPaymentProcessor: processorAttr.enabled, paymentProcessorId: processorAttr.value?.id ?? null,
          }
        );
        notify(t('hotelAttrsDone', {
          hotels: response.data.affectedHotels,
          leads: response.data.affectedLeads,
        }), { type: 'info' });
      }
      notify(t('done', { count: selectedIds.length }), { type: 'info' });
      unselectAll();
      refresh();
      onClose();
    } catch {
      notify(t('error'), { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('title', { count: selectedIds.length })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('fieldsSection')}</Typography>
        <CatalogRow label={translate('resources.leads.fields.companyName')} resource="companies" state={company} onChange={setCompany} />
        <CatalogRow label={translate('resources.leads.fields.hotelName')} resource="hotels" state={hotel} onChange={setHotel} />
        <CatalogRow label={translate('resources.leads.fields.hotelChainName')} resource="hotel-chains" state={chain} onChange={setChain} />
        <CatalogRow label={translate('resources.leads.fields.jobTitleName')} resource="job-titles" state={jobTitle} onChange={setJobTitle} />
        <CatalogRow label={translate('resources.leads.fields.contactTypeName')} resource="contact-types" state={contactType} onChange={setContactType} />
        <CatalogRow label={translate('resources.leads.fields.leadSourceName')} resource="lead-sources" state={leadSource} onChange={setLeadSource} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Checkbox size="small" checked={countryEnabled} onChange={(e) => setCountryEnabled(e.target.checked)} />
          <TextField
            size="small"
            disabled={!countryEnabled}
            label={translate('resources.leads.fields.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Checkbox size="small" checked={statusEnabled} onChange={(e) => setStatusEnabled(e.target.checked)} />
          <TextField
            select
            size="small"
            disabled={!statusEnabled}
            label={translate('resources.leads.fields.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ flex: 1 }}
          >
            {CANONICAL_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('setsSection')}</Typography>
        <SetItemsBlock
          label={translate('resources.leads.fields.tagNames')}
          resource="tags"
          mode={tagMode}
          items={tagItems}
          onMode={setTagMode}
          onItems={setTagItems}
          modeLabels={modeLabels}
        />
        <SetItemsBlock
          label={translate('resources.leads.fields.interestNames')}
          resource="interests"
          mode={interestMode}
          items={interestItems}
          onMode={setInterestMode}
          onItems={setInterestItems}
          modeLabels={modeLabels}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">{t('hotelAttrsSection')}</Typography>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          {t('hotelAttrsWarning')}
        </Typography>
        <CatalogRow label={translate('resources.hotels.fields.hotelChainName')} resource="hotel-chains" state={hotelChainAttr} onChange={setHotelChainAttr} />
        <CatalogRow label={translate('resources.hotels.fields.hotelCategoryName')} resource="hotel-categories" state={hotelCategoryAttr} onChange={setHotelCategoryAttr} />
        <CatalogRow label={translate('resources.hotels.fields.pmsSystemName')} resource="pms-systems" state={pmsAttr} onChange={setPmsAttr} />
        <CatalogRow label={translate('resources.hotels.fields.lockSystemName')} resource="lock-systems" state={lockAttr} onChange={setLockAttr} />
        <CatalogRow label={translate('resources.hotels.fields.paymentProcessorName')} resource="payment-processors" state={processorAttr} onChange={setProcessorAttr} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
        <Button variant="contained" onClick={handleApply}>{t('apply')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const LeadBulkActionButtons = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('leads');
  const { selectedIds } = useListContext();
  const { permissions } = usePermissions<string>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`resources.leads.bulk.${key}`, options);

  const canArchive = permissions === 'superuser' || permissions === 'comercial';
  const canPurge = permissions === 'superuser';

  const run = async (path: string, confirmKey?: string) => {
    if (confirmKey && !window.confirm(t(confirmKey, { count: selectedIds.length }))) return;
    try {
      await httpClient.post(`/leads/bulk/${path}`, { leadIds: selectedIds });
      notify(t('done', { count: selectedIds.length }), { type: 'info' });
      unselectAll();
      refresh();
    } catch {
      notify(t('error'), { type: 'error' });
    }
  };

  return (
    <>
      <Button size="small" startIcon={<GroupEditIcon />} onClick={() => setDialogOpen(true)}>
        {t('groupEdit')}
      </Button>
      {canArchive && (
        <Button size="small" startIcon={<ArchiveIcon />} onClick={() => run('archive', 'archiveConfirm')}>
          {translate('resources.leads.actions.archive')}
        </Button>
      )}
      {canArchive && (
        <Button size="small" startIcon={<RestoreIcon />} onClick={() => run('restore')}>
          {translate('resources.leads.actions.restore')}
        </Button>
      )}
      {canPurge && (
        <Button
          size="small"
          color="error"
          startIcon={<PurgeIcon />}
          onClick={() => run('purge', 'purgeConfirm')}
        >
          {t('purge')}
        </Button>
      )}
      {dialogOpen && <BulkEditDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />}
    </>
  );
};

export default LeadBulkActionButtons;
